import React, { createContext, useState, useEffect, useContext } from 'react';
import socketServices from '../utility/socketService';
import Cookies from 'universal-cookie';
import { COMPANY_ID, COMPANY_EMPLOYEE_ID, TOKEN_NAME, PRODUCT_SLUG, IS_CHAT } from '../config';
import toast from 'react-hot-toast';
import ChatNotification from '../components/notifications/ChatNotification';
import { useNavigate, useLocation } from 'react-router-dom';

// Create context
export const SocketContext = createContext(null);

// Custom hook for accessing the socket context
export const useSocket = () => {
  const context = useContext(SocketContext);
  if (!context) {
    throw new Error('useSocket must be used within a SocketProvider');
  }
  return context;
};

export const SocketProvider = ({ children }) => {
  const cookies = new Cookies();
  const navigate = useNavigate();
  const location = useLocation(); 
  
  const [isConnected, setIsConnected] = useState(false);
  const [hasInitialized, setHasInitialized] = useState(false);
  const [connectionAttempts, setConnectionAttempts] = useState(0);
  const [chatList, setChatList] = useState([]);
  const [isInitialChatListFetched, setIsInitialChatListFetched] = useState(false);
  const [activeChatRoom, setActiveChatRoom] = useState(null);
  const [prevPathname, setPrevPathname] = useState(location.pathname);

  // Check for required cookies
  const hasRequiredCookies = () => {
    const token = cookies.get(TOKEN_NAME);
    const companyId = cookies.get(COMPANY_ID);
    const employeeId = cookies.get(COMPANY_EMPLOYEE_ID);
    const isChat = cookies.get(IS_CHAT) === "true" || cookies.get(IS_CHAT) === true;
    return token && companyId && employeeId && isChat;
  };

  // Initialize socket connection
  const initializeSocket = () => {
    // Skip if socket is already initialized or cookies are missing
    if (hasInitialized || !hasRequiredCookies()) {
      // console.log("Socket already initialized or missing required cookies");
      return;
    }

    // console.log("Initializing global socket connection");
    socketServices.initializeSocket();
    setHasInitialized(true);

    // Listen for connection events
    socketServices.on("connect", () => {
      console.log("Socket connected globally");
      setIsConnected(true);
      
      // Send connection event with user data
      const companyId = Number(cookies.get(COMPANY_ID));
      const employeeId = Number(cookies.get(COMPANY_EMPLOYEE_ID));
      
      // Emit connection-event
      socketServices.emit(
        "connection-event",
        {
          self_employee_id: employeeId,
          company_id: companyId,
          product_slug: PRODUCT_SLUG
        },
        (response) => {
          console.log("Connection event response:", response);
          if (response?.success) {
            console.log("User connected to socket server successfully");
          } else {
            console.error("Error connecting to socket server:", response);
          }
        }
      );
      
      // Reset connection attempts on successful connection
      setConnectionAttempts(0);
    });

    // Handle disconnection
    socketServices.on("disconnect", () => {
      console.log("Socket disconnected globally");
      setIsConnected(false);
    });

    // Handle connection error
    socketServices.on("connect_error", (error) => {
      // console.error("Socket connection error:", error);
      setConnectionAttempts(prev => prev + 1);
      
      // If multiple connection attempts fail, notify the user
      if (connectionAttempts > 2) {
        toast.error("Having trouble connecting to the socket server. Please check your connection.");
      }
    });

    // Listen for chat-list events for real-time updates
    socketServices.on("chat-list", handleChatListUpdate);
  };

  // Functions to register and unregister active chat rooms
  const registerChatRoom = (chatId) => {
    if (chatId) {
      // console.log(`Registering active chat room: ${chatId}`);
      setActiveChatRoom(chatId);
    }
  };

  const unregisterChatRoom = (chatId) => {
    if (activeChatRoom === chatId) {
      // console.log(`Unregistering active chat room: ${chatId}`);
      setActiveChatRoom(null);
    }
  };

  // Handle leaving active chat room when navigating away
  const leaveActiveChatRoom = () => {
    if (activeChatRoom && isConnected) {
      // console.log(`Leaving active chat room ${activeChatRoom} due to page navigation`);
      socketServices.emit("leave-room", { chat_id: activeChatRoom });
      setActiveChatRoom(null);
      return true;
    }
    return false;
  };

  // Add listener for page navigation/unload to handle active chat rooms
  useEffect(() => {
    const handleBeforeUnload = (e) => {
      const currentPath = window.location.pathname;
      if (currentPath === '/chats') {
        // We're on the chat page, let the components handle their cleanup
        return;
      }
      
      // If we're navigating away from chats and have an active room, leave it
      if (activeChatRoom && isConnected) {
        console.log(`Navigation detected - leaving chat room ${activeChatRoom}`);
        socketServices.emit("leave-room", { chat_id: activeChatRoom });
      }
    };

    window.addEventListener('beforeunload', handleBeforeUnload);
    
    return () => {
      window.removeEventListener('beforeunload', handleBeforeUnload);
    };
  }, [activeChatRoom, isConnected]);

  // Use useLocation to track route changes and leave chat rooms when navigating away from /chats
  useEffect(() => {
    // Check if we navigated away from /chats
    if (prevPathname === '/chats' && location.pathname !== '/chats' && activeChatRoom) {
      // console.log(`Route changed from /chats to ${location.pathname}, leaving active chat room`);
      leaveActiveChatRoom();
    }
    
    // Update previous pathname
    setPrevPathname(location.pathname);
  }, [location.pathname, activeChatRoom, isConnected]);

  // Handle incoming chat list updates
  const handleChatListUpdate = (chatData) => {
    // console.log("Received chat-list update:", chatData);
    
    if (!chatData) return;

    // Check if we're on the chats page
    const isOnChatPage = window.location.pathname === '/chats';
    
    // Update chatList state by adding new chat or updating existing one
    // (Only needed for notifications, not for refreshing the chat list on the chats page)
    setChatList(prevList => {
      // Check if this chat already exists in our list
      const existingIndex = prevList.findIndex(chat => chat.chat_id === chatData.chat_id);
      
      // Create a copy of the list
      let updatedList = [...prevList];
      
      if (existingIndex !== -1) {
        // Update existing chat
        updatedList[existingIndex] = {
          ...updatedList[existingIndex],
          chat: chatData.chat,
          other_member: chatData.other_member.select
        };
      } else {
        // Add new chat
        updatedList.push({
          chat_id: chatData.chat_id,
          other_member_id: chatData.other_member_id,
          other_member: chatData.other_member.select,
          chat: chatData.chat
        });
      }
      
      // Sort by last_message_at to keep most recent at top
      return updatedList.sort((a, b) => {
        const timeA = new Date(a.chat?.last_message_at || 0);
        const timeB = new Date(b.chat?.last_message_at || 0);
        return timeB - timeA; // Descending order (newest first)
      });
    });
    
    // Only show notification if not on chat page
    if (!isOnChatPage) {
      // Get the sender's name
      const firstName = chatData.other_member.select.first_name;
      const lastName = chatData.other_member.select.last_name;
      
      // Get the message content
      const messageContent = chatData.chat.last_chat_message?.message || '';
      
      // Get sender's avatar if available
      const senderAvatar = chatData.other_member.select.image_path || null;
      
      // Determine the appropriate notification based on message type
      if (chatData.chat.last_chat_message?.msg_type === 'needs_attention') {
        ChatNotification({
          firstName: firstName,
          lastName: lastName,
          message: "Needs your attention!",
          avatar: senderAvatar,
          needsAttention: true,
          chatId: chatData.chat_id,
          onClickHandler: (url) => {
            if (url) {
              navigate(url);
            } else {
              navigate('/chats');
            }
          }
        });
      } else {
        // Regular message notification
        ChatNotification({
          firstName: firstName,
          lastName: lastName,
          message: messageContent,
          avatar: senderAvatar,
          chatId: chatData.chat_id,
          onClickHandler: (url) => {
            if (url) {
              navigate(url);
            } else {
              navigate('/chats');
            }
          }
        });
      }
    }
  };

  // Update chatList with initial data from API
  const setInitialChatList = (initialData) => {
    if (initialData && Array.isArray(initialData) && !isInitialChatListFetched) {
      setChatList(initialData);
      setIsInitialChatListFetched(true);
    }
  };

  // Disconnect socket
  const disconnectSocket = () => {
    if (socketServices.socket) {
      // If there's an active chat room, leave it first
      if (activeChatRoom && isConnected) {
        socketServices.emit("leave-room", { chat_id: activeChatRoom });
        setActiveChatRoom(null);
      }
      
      // console.log("Disconnecting socket globally");
      socketServices.disconnect();
      setIsConnected(false);
      setHasInitialized(false);
    }
  };

  // Add cleanup on unmount
  useEffect(() => {
    return () => {
      disconnectSocket();
    };
  }, []);

  useEffect(() => {
    const handleBeforeUnload = () => {
      // console.log("Page is being refreshed or closed, disconnecting socket");
      disconnectSocket();
    };

    window.addEventListener('beforeunload', handleBeforeUnload);
    
    return () => {
      window.removeEventListener('beforeunload', handleBeforeUnload);
    };
  }, []);

  // Monitor connection status from socketServices
  useEffect(() => {
    if (!hasInitialized) return;

    const checkConnectionStatus = () => {
      const connected = socketServices.isConnected();
      if (connected !== isConnected) {
        setIsConnected(connected);
      }
    };
    
    // Check every 3 seconds
    const interval = setInterval(checkConnectionStatus, 3000);
    
    return () => clearInterval(interval);
  }, [isConnected, hasInitialized]);

  // Provide context value
  const value = {
    isConnected,
    initializeSocket,
    disconnectSocket,
    chatList,
    setInitialChatList,
    registerChatRoom,
    unregisterChatRoom,
    leaveActiveChatRoom,
    emit: socketServices.emit.bind(socketServices),
    on: socketServices.on.bind(socketServices),
    removeListener: socketServices.removeListener.bind(socketServices),
  };

  return (
    <SocketContext.Provider value={value}>
      {children}
    </SocketContext.Provider>
  );
};
